// @flow

import { all, put, takeLatest, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/employee/actionCreator";
import recordingActionCreators from "@modules/recording/actionCreator";
import {
  UpdateEmployeeDetails,
  UpdateEmployeeEmail,
  AddAlertExclusion,
  UpdateAlertExclusion,
  GetEmployeeAlertExclusions,
  RemoveAlertExclusion,
  GetClientAlert,
  GetEmployeeAlertExclusionSchema,
} from "@modules/employee/constants/actions";
import type { EmployeeFormData } from "@modules/employee/types";
import type { PaginationType } from "@types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ClientRequest = $Exact<{
  clientId: string,
}>;

type EmployeeRequest = $Exact<{
  ...ClientRequest,
  employeeId: string,
  page?: number,
}>;
type UpdateEmployeeRequest = $Exact<{
  ...EmployeeRequest,
  formData: EmployeeFormData,
  absenceId?: number,
}>;

type UpdateAlertExclusionRequest = $Exact<{
  ...UpdateEmployeeRequest,
  employeeAlertExclusionId: number,
}>;

type RemoveEmployeeAlertExclusion = $Exact<{
  ...EmployeeRequest,
  employeeAlertExclusionId: number,
}>;

type ClientAlertRequest = $Exact<{
  ...ClientRequest,
  alertId: number,
}>;

export function* onGetEmployeeAlertExclusions({
  payload,
}: Action<EmployeeRequest>): Generator<
  *,
  void,
  $Exact<{
    data: {
      content: any,
      pagination: PaginationType,
      totalElements: number,
    },
  }>,
> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion`,
    );
    yield put(actionCreators.GetEmployeeAlertExclusionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAlertExclusionsError(e.response));
  }
}

export function* onUpdateEmployeeDetails({
  payload,
}: Action<UpdateEmployeeRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}`,
      {
        ...payload.formData,
        reasonForUpdate: "NOT_DISCLOSED",
      },
    );

    yield all([
      put(
        recordingActionCreators.GetEmployeeAbsenceRecording({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.employeeId,
          },
        }),
      ),
      put(actionCreators.UpdateEmployeeDetailsSuccess(response.data)),
      put(actionCreators.TogglePersonalDetailsModal("")),
      put(sendSuccessMessage("EMPLOYEE_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeDetailsError(e.response));
  }
}
export function* onUpdateEmployeeEmail({
  payload,
}: Action<{ employeeId: number, clientId: number, workEmail: string }>): Generator<
  *,
  void,
  Object,
> {
  const { employeeId, clientId, workEmail } = payload || {};
  try {
    if (!employeeId || !clientId || !workEmail) {
      throw Error("Invalid payload provided");
    }
    const { data } = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}`,
    );

    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}`,
      {
        ...data,
        contactDetails: {
          workEmail: workEmail,
        },
      },
    );
    yield all([
      put(actionCreators.UpdateEmployeeDetailsSuccess(response.data)),
      put(
        recordingActionCreators.GetEmployeeAbsenceRecording({
          clientId: clientId,
          employeeId: employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield all([
      put(actionCreators.UpdateEmployeeDetailsError(e.response)),
      put(
        recordingActionCreators.GetEmployeeAbsenceRecording({
          clientId,
          employeeId,
        }),
      ),
    ]);
  }
}

export function* onAddAlertExclusion({
  payload,
}: Action<UpdateEmployeeRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion`,
      payload.formData,
    );

    yield all([
      put(sendSuccessMessage("ALERT_EXCLUSION_ADDED")),
      put(actionCreators.SetShowExcludeEmployeeFromAlertsAddModal({ visible: false })),
      put(actionCreators.ClearDataAlertExlusionForm()),
      put(
        actionCreators.GetEmployeeAlertExclusions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAlertExclusionError(e.response));
  }
}

export function* onUpdateAlertExclusion({
  payload,
}: Action<UpdateAlertExclusionRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion/${payload.employeeAlertExclusionId}`,
      payload.formData,
    );

    yield all([
      put(sendSuccessMessage("ALERT_EXCLUSION_UPDATED")),
      put(actionCreators.SetShowEditAlertExclusionModal({ visible: false })),
      put(actionCreators.ClearDataAlertExlusionForm()),
      put(
        actionCreators.GetEmployeeAlertExclusions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAlertExclusionError(e.response));
  }
}

export function* onRemoveAlertExclusion({
  payload,
}: Action<RemoveEmployeeAlertExclusion>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.employeeAlertExclusionId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion/${payload.employeeAlertExclusionId}`,
    );
    yield all([
      put(actionCreators.SetShowRemoveAlertExclusionModal({ visible: false })),
      put(sendSuccessMessage("ALERT_EXCLUSION_REMOVED")),
      put(
        actionCreators.GetEmployeeAlertExclusions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveAlertExclusionError(e.response));
  }
}

export function* onGetClientAlert({
  payload,
}: Action<ClientAlertRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );
    yield all([put(actionCreators.GetClientAlertSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetClientAlertError(e.response));
  }
}

//TODO: Seems unused
export function* onGetEmployeeAlertExclusionSchema({
  payload,
}: Action<ClientRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const schema = yield parseSchema("EmployeeAlertExclusionDTO", { clientId: payload.clientId });

    yield all([put(actionCreators.SetEmployeeAlertExclusionSchema(schema))]);
  } catch (e) {
    yield put(actionCreators.GetEmployeeAlertExclusionSchemaError(e.response));
  }
}

export function* PersonalDetailsSagas(): Generator<*, void, void> {
  yield all([takeEvery(GetEmployeeAlertExclusions, onGetEmployeeAlertExclusions)]);
  yield all([takeLatest(UpdateEmployeeDetails, onUpdateEmployeeDetails)]);
  yield all([takeLatest(UpdateEmployeeEmail, onUpdateEmployeeEmail)]);
  yield all([takeEvery(AddAlertExclusion, onAddAlertExclusion)]);
  yield all([takeEvery(UpdateAlertExclusion, onUpdateAlertExclusion)]);
  yield all([takeEvery(RemoveAlertExclusion, onRemoveAlertExclusion)]);
  yield all([takeEvery(GetClientAlert, onGetClientAlert)]);
  yield all([takeEvery(GetEmployeeAlertExclusionSchema, onGetEmployeeAlertExclusionSchema)]);
}

export default PersonalDetailsSagas;
